/**
 * Variable substitution for quick-reply template bodies.
 *
 * Rendered client-side at insert time (not on send) so the operator
 * sees the realised text in the textarea and can edit it before
 * sending. This is the inverse of how most email-template systems
 * work but it's what operators want: WYSIWYG over what the customer
 * will see, no surprises after Enter.
 *
 * Placeholder grammar (intentionally simple):
 *   {{customer.first_name}}   {{customer.last_name}}   {{customer.display_name}}
 *   {{operator.first_name}}   {{operator.last_name}}   {{operator.display_name}}
 *   {{tenant.name}}
 *
 * Whitespace inside the braces is tolerated: `{{ customer.first_name }}`
 * works too.
 *
 * Resolution rules:
 *   - Known placeholder with a non-empty value → replace with the value.
 *   - Known placeholder with null / empty value → replace with the
 *     empty string. The operator sees "Hi !" and can correct it before
 *     sending. This is intentional: we'd rather surface missing data
 *     than swap in a generic "there" and have the customer realise
 *     we didn't actually know their name.
 *   - Unknown placeholder (typo, future variable) → leave literal.
 *     Operator sees `{{custmer.first_name}}` and knows to fix it.
 *
 * No conditionals, no fallback syntax (`{{foo|default}}`), no nested
 * lookups. Keep the surface small until operators ask for more.
 */

export interface PersonNameContext {
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
}

export interface TenantContext {
  name: string | null;
}

export interface TemplateContext {
  customer: PersonNameContext | null;
  operator: PersonNameContext | null;
  tenant: TenantContext | null;
}

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*\.[a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;

/**
 * Catalogue of supported placeholders. Exported so the admin form can
 * render a "Available variables" hint without duplicating the list.
 */
export const SUPPORTED_PLACEHOLDERS = [
  'customer.first_name',
  'customer.last_name',
  'customer.display_name',
  'operator.first_name',
  'operator.last_name',
  'operator.display_name',
  'tenant.name',
] as const;

export type SupportedPlaceholder = (typeof SUPPORTED_PLACEHOLDERS)[number];

export function renderTemplate(body: string, ctx: TemplateContext): string {
  return body.replace(PLACEHOLDER_RE, (whole, key: string) => {
    const value = resolveKey(key, ctx);
    if (value === null) return whole; // unknown → leave literal
    return value; // known → use value (empty string if missing)
  });
}

function resolveKey(key: string, ctx: TemplateContext): string | null {
  switch (key) {
    case 'customer.first_name':
      return safeString(ctx.customer?.firstName);
    case 'customer.last_name':
      return safeString(ctx.customer?.lastName);
    case 'customer.display_name':
      return safeString(ctx.customer?.displayName);
    case 'operator.first_name':
      return safeString(ctx.operator?.firstName);
    case 'operator.last_name':
      return safeString(ctx.operator?.lastName);
    case 'operator.display_name':
      return safeString(ctx.operator?.displayName);
    case 'tenant.name':
      return safeString(ctx.tenant?.name);
    default:
      return null;
  }
}

function safeString(value: string | null | undefined): string {
  if (value === null || value === undefined) return '';
  return value;
}
